/**
 * Stats Badge Component
 * Compact badge showing stats with dropdown for details
 */

import { useEffect, useRef, useState } from 'react'
import type { Stats } from '../../types'

interface StatsBadgeProps {
  stats: Stats
  currentStreak: number
}

export default function StatsBadge({ stats, currentStreak }: StatsBadgeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const badgeRef = useRef<HTMLDivElement | null>(null)

  // Calculate progress for streak ring (0-100%)
  const maxStreakForRing = 10
  const streakProgress = Math.min(currentStreak / maxStreakForRing, 1) * 100
  const circumference = 2 * Math.PI * 16 // radius = 16
  const strokeDashoffset = circumference - (streakProgress / 100) * circumference

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (badgeRef.current && !badgeRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <div className="stats-badge" ref={badgeRef}>
      <button
        className="stats-badge__toggle"
        id="statsBadgeToggle"
        type="button"
        aria-expanded={isOpen}
        aria-controls="statsBadgeDropdown"
        aria-label="Mission stats"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="stats-badge__section">
          <span className="stats-badge__emoji">🎯</span>
          <span className="stats-badge__value">{stats.solved}</span>
        </div>
        <div className="stats-badge__divider"></div>
        <div className="stats-badge__section stats-badge__section--streak">
          <div className="stats-badge__ring-container">
            <svg
              className="stats-badge__ring"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              aria-hidden="true"
            >
              <circle
                className="stats-badge__ring-track"
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke="var(--stats-ring-track)"
                strokeWidth="3"
              />
              <circle
                className="stats-badge__ring-progress"
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke="var(--stats-ring-progress)"
                strokeWidth="3"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 20 20)"
              />
            </svg>
            <span className="stats-badge__ring-emoji">🔥</span>
          </div>
          <span className="stats-badge__value">{currentStreak}</span>
        </div>
        <div className="stats-badge__divider"></div>
        <div className="stats-badge__section">
          <span className="stats-badge__emoji">🏆</span>
          <span className="stats-badge__value">{stats.bestStreak}</span>
        </div>
        <svg
          className="stats-badge__chevron"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="stats-badge__dropdown" id="statsBadgeDropdown">
          <div className="stats-badge__dropdown-content">
            <h3 className="stats-badge__dropdown-title">Mission Stats</h3>
            <div className="stats-badge__dropdown-grid">
              <div className="stats-badge__dropdown-item">
                <span className="stats-badge__dropdown-emoji">🎯</span>
                <div>
                  <div className="stats-badge__dropdown-label">Problems Solved</div>
                  <div className="stats-badge__dropdown-value">{stats.solved}</div>
                </div>
              </div>
              <div className="stats-badge__dropdown-item">
                <span className="stats-badge__dropdown-emoji">🔥</span>
                <div>
                  <div className="stats-badge__dropdown-label">Current Streak</div>
                  <div className="stats-badge__dropdown-value">{currentStreak}</div>
                </div>
              </div>
              <div className="stats-badge__dropdown-item">
                <span className="stats-badge__dropdown-emoji">🏆</span>
                <div>
                  <div className="stats-badge__dropdown-label">Best Streak</div>
                  <div className="stats-badge__dropdown-value">{stats.bestStreak}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
